import { useState } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  BOOK_TYPE_DESCRIPTIONS,
  BOOK_TYPE_LABELS,
  BOOK_TYPES,
  type BookTypeId,
  type CreateBookOptions,
} from "../../../shared/bookType";
import { cn } from "../../lib/utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (options: CreateBookOptions) => void;
};

export function BookTypePickerDialog({ open, onOpenChange, onConfirm }: Props) {
  const [selected, setSelected] = useState<BookTypeId>("fiction");
  const [articleStep, setArticleStep] = useState(false);

  function resetAndClose() {
    setArticleStep(false);
    setSelected("fiction");
    onOpenChange(false);
  }

  function pickType(id: BookTypeId) {
    if (id === "article") {
      setSelected(id);
      setArticleStep(true);
      return;
    }
    onConfirm({ bookType: id });
    resetAndClose();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) resetAndClose();
        else onOpenChange(true);
      }}
    >
      <DialogContent className="max-w-lg" aria-describedby="book-type-picker-desc">
        <DialogHeader>
          <DialogTitle>{articleStep ? "Article structure" : "What are you writing?"}</DialogTitle>
          <DialogDescription id="book-type-picker-desc">
            {articleStep
              ? "A single piece, or a collection of articles in one project."
              : "This sets the spine labels and which tools show. You can change it later in Settings."}
          </DialogDescription>
        </DialogHeader>

        {articleStep ? (
          <div className="grid gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-auto items-start justify-start whitespace-normal py-3 text-left"
              onClick={() => {
                onConfirm({ bookType: "article", structure: "single" });
                resetAndClose();
              }}
            >
              <span className="flex flex-col gap-1">
                <span className="font-semibold">Single article</span>
                <span className="text-xs font-normal text-muted-foreground">
                  One document. No chapter list to manage.
                </span>
              </span>
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-auto items-start justify-start whitespace-normal py-3 text-left"
              onClick={() => {
                onConfirm({ bookType: "article", structure: "collection" });
                resetAndClose();
              }}
            >
              <span className="flex flex-col gap-1">
                <span className="font-semibold">Collection of articles</span>
                <span className="text-xs font-normal text-muted-foreground">
                  Several articles in one project, without chapter numbers.
                </span>
              </span>
            </Button>
          </div>
        ) : (
          <div className="grid gap-2">
            {BOOK_TYPES.map((id) => (
              <button
                key={id}
                type="button"
                className={cn(
                  "rounded-lg border border-border bg-card px-3 py-2.5 text-left hover:bg-muted/40",
                  selected === id && "border-primary/40 ring-1 ring-primary/20"
                )}
                onClick={() => pickType(id)}
              >
                <span className="block text-sm font-semibold text-foreground">{BOOK_TYPE_LABELS[id]}</span>
                <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                  {BOOK_TYPE_DESCRIPTIONS[id]}
                </span>
              </button>
            ))}
          </div>
        )}

        <DialogFooter>
          {articleStep ? (
            <Button type="button" variant="ghost" onClick={() => setArticleStep(false)}>
              Back
            </Button>
          ) : (
            <Button type="button" variant="ghost" onClick={resetAndClose}>
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
